import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { 
  User, 
  Building2, 
  MapPin, 
  Plus, 
  Trash2, 
  Phone, 
  Mail, 
  Globe,
  Briefcase,
  ChevronRight,
  ChevronLeft,
  Check,
  Loader2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const OnboardingModal = ({ isOpen, onClose, onComplete }) => {
  const { user, refreshUser } = useAuth();
  const { t } = useLanguage();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [prefillLoading, setPrefillLoading] = useState(true);
  
  // Personal Info State
  const [personalInfo, setPersonalInfo] = useState({
    full_name: '',
    email: '',
    phone: '',
    phone_extension: '',
    mobile: '',
    preferred_contact_method: 'email',
    preferred_language: 'en',
    job_title: ''
  });
  
  // Company Info State
  const [companyInfo, setCompanyInfo] = useState({
    company_name: '',
    company_email: '',
    website: '',
    industry: ''
  });
  
  // Locations State
  const [locations, setLocations] = useState([
    { 
      name: 'Main Office', 
      address: '', 
      city: '', 
      province: '', 
      postal_code: '', 
      country: 'Canada',
      phone: '',
      email: '',
      is_primary: true 
    }
  ]);

  // Fetch prefill data on mount
  useEffect(() => {
    if (isOpen) {
      fetchPrefillData();
    }
  }, [isOpen]);

  const fetchPrefillData = async () => {
    setPrefillLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/onboarding/prefill`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        
        setPersonalInfo({
          full_name: data.personal?.full_name || user?.full_name || '',
          email: data.personal?.email || user?.email || '',
          phone: data.personal?.phone || '',
          phone_extension: data.personal?.phone_extension || '',
          mobile: data.personal?.mobile || '',
          preferred_contact_method: data.personal?.preferred_contact_method || 'email',
          preferred_language: data.personal?.preferred_language || 'en',
          job_title: data.personal?.job_title || ''
        });
        
        setCompanyInfo({
          company_name: data.company?.company_name || '',
          company_email: data.company?.company_email || '',
          website: data.company?.website || '',
          industry: data.company?.industry || ''
        });
        
        if (data.company?.locations?.length > 0) {
          setLocations(data.company.locations);
        }
      }
    } catch (error) {
      console.error('Error fetching prefill data:', error);
    } finally {
      setPrefillLoading(false);
    }
  };

  const addLocation = () => {
    setLocations([...locations, {
      name: '',
      address: '',
      city: '',
      province: '',
      postal_code: '',
      country: 'Canada',
      phone: '',
      email: '',
      is_primary: false
    }]);
  };

  const removeLocation = (index) => {
    if (locations.length <= 1) return;
    const newLocations = locations.filter((_, i) => i !== index);
    // If we removed the primary, make the first one primary
    if (locations[index].is_primary && newLocations.length > 0) {
      newLocations[0].is_primary = true;
    }
    setLocations(newLocations);
  };

  const updateLocation = (index, field, value) => {
    const newLocations = [...locations];
    newLocations[index][field] = value;
    
    // If setting as primary, unset others
    if (field === 'is_primary' && value) {
      newLocations.forEach((loc, i) => {
        if (i !== index) loc.is_primary = false;
      });
    }
    
    setLocations(newLocations);
  };

  const handleSubmit = async () => {
    // Validation
    if (!personalInfo.full_name.trim()) {
      toast.error('Please enter your full name');
      setStep(1);
      return;
    }
    if (!companyInfo.company_name.trim()) {
      toast.error('Please enter your company name');
      setStep(2);
      return;
    }
    if (locations.length === 0 || !locations[0].name.trim()) {
      toast.error('Please add at least one location');
      setStep(3);
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/onboarding/complete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          personal: personalInfo,
          company: {
            ...companyInfo,
            locations
          }
        })
      });

      if (response.ok) {
        toast.success('Profile setup complete!');
        if (refreshUser) await refreshUser();
        onComplete?.();
        onClose();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save profile');
      }
    } catch (error) {
      console.error('Onboarding error:', error);
      toast.error('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderStepIndicator = () => (
    <div className="flex items-center justify-center gap-2 mb-6">
      {[1, 2, 3].map((s) => (
        <div key={s} className="flex items-center">
          <div 
            className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
              s === step 
                ? 'bg-[#1e3a5f] text-white' 
                : s < step 
                  ? 'bg-green-500 text-white' 
                  : 'bg-slate-200 text-slate-500'
            }`}
          >
            {s < step ? <Check className="h-4 w-4" /> : s}
          </div>
          {s < 3 && (
            <div className={`w-12 h-1 mx-1 ${s < step ? 'bg-green-500' : 'bg-slate-200'}`} />
          )}
        </div>
      ))}
    </div>
  );

  const renderStep1 = () => (
    <div className="space-y-4">
      <div className="flex items-center gap-2 mb-4">
        <User className="h-5 w-5 text-[#1e3a5f]" />
        <h3 className="text-lg font-semibold">Personal Information</h3>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label>Full Name *</Label>
          <Input
            value={personalInfo.full_name}
            onChange={(e) => setPersonalInfo({...personalInfo, full_name: e.target.value})}
            placeholder="John Smith"
            data-testid="onboard-fullname"
          />
        </div>
        <div>
          <Label>Email *</Label>
          <Input
            type="email"
            value={personalInfo.email}
            onChange={(e) => setPersonalInfo({...personalInfo, email: e.target.value})}
            disabled
            className="bg-slate-100"
          />
        </div>
        <div>
          <Label>Phone</Label>
          <Input
            value={personalInfo.phone}
            onChange={(e) => setPersonalInfo({...personalInfo, phone: e.target.value})}
            placeholder="+1 (555) 123-4567"
            data-testid="onboard-phone"
          />
        </div>
        <div>
          <Label>Extension</Label>
          <Input
            value={personalInfo.phone_extension}
            onChange={(e) => setPersonalInfo({...personalInfo, phone_extension: e.target.value})}
            placeholder="123"
            data-testid="onboard-extension"
          />
        </div>
        <div>
          <Label>Mobile</Label>
          <Input
            value={personalInfo.mobile}
            onChange={(e) => setPersonalInfo({...personalInfo, mobile: e.target.value})}
            placeholder="+1 (555) 987-6543"
          />
        </div>
        <div>
          <Label>Job Title</Label>
          <Input
            value={personalInfo.job_title}
            onChange={(e) => setPersonalInfo({...personalInfo, job_title: e.target.value})}
            placeholder="IT Manager"
          />
        </div>
        <div>
          <Label>Preferred Contact Method</Label>
          <Select 
            value={personalInfo.preferred_contact_method}
            onValueChange={(v) => setPersonalInfo({...personalInfo, preferred_contact_method: v})}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="email">Email</SelectItem>
              <SelectItem value="phone">Phone</SelectItem>
              <SelectItem value="mobile">Mobile</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Preferred Language</Label>
          <Select 
            value={personalInfo.preferred_language}
            onValueChange={(v) => setPersonalInfo({...personalInfo, preferred_language: v})}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="en">English</SelectItem>
              <SelectItem value="fr">Français</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-4">
      <div className="flex items-center gap-2 mb-4">
        <Building2 className="h-5 w-5 text-[#1e3a5f]" />
        <h3 className="text-lg font-semibold">Company Information</h3>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <Label>Company Name *</Label>
          <Input
            value={companyInfo.company_name}
            onChange={(e) => setCompanyInfo({...companyInfo, company_name: e.target.value})}
            placeholder="Acme Corporation"
            data-testid="onboard-company-name"
          />
        </div>
        <div>
          <Label>Company Email</Label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              type="email"
              value={companyInfo.company_email}
              onChange={(e) => setCompanyInfo({...companyInfo, company_email: e.target.value})}
              placeholder="info@company.com"
              className="pl-10"
            />
          </div>
        </div>
        <div>
          <Label>Website</Label>
          <div className="relative">
            <Globe className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              value={companyInfo.website}
              onChange={(e) => setCompanyInfo({...companyInfo, website: e.target.value})}
              placeholder="https://www.company.com"
              className="pl-10"
            />
          </div>
        </div>
        <div className="md:col-span-2">
          <Label>Industry</Label>
          <div className="relative">
            <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              value={companyInfo.industry}
              onChange={(e) => setCompanyInfo({...companyInfo, industry: e.target.value})}
              placeholder="Technology, Healthcare, Finance, etc."
              className="pl-10"
            />
          </div>
        </div>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <MapPin className="h-5 w-5 text-[#1e3a5f]" />
          <h3 className="text-lg font-semibold">Company Locations</h3>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={addLocation}
          className="text-[#1e3a5f]"
        >
          <Plus className="h-4 w-4 mr-1" />
          Add Location
        </Button>
      </div>
      
      <div className="space-y-4 max-h-[400px] overflow-y-auto pr-2">
        {locations.map((location, index) => (
          <div 
            key={index}
            className={`p-4 rounded-lg border ${location.is_primary ? 'border-[#1e3a5f] bg-blue-50/50' : 'border-slate-200'}`}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <span className="font-medium text-slate-900">
                  {companyInfo.company_name || 'Company'} - {location.name || 'Location ' + (index + 1)}
                </span>
                {location.is_primary && (
                  <span className="text-xs bg-[#1e3a5f] text-white px-2 py-0.5 rounded">Primary</span>
                )}
              </div>
              {locations.length > 1 && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => removeLocation(index)}
                  className="text-red-500 hover:text-red-700 hover:bg-red-50"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Location Name *</Label>
                <Input
                  value={location.name}
                  onChange={(e) => updateLocation(index, 'name', e.target.value)}
                  placeholder="Montreal HQ"
                  className="h-9"
                />
              </div>
              <div>
                <Label className="text-xs">Phone</Label>
                <Input
                  value={location.phone}
                  onChange={(e) => updateLocation(index, 'phone', e.target.value)}
                  placeholder="+1 (555) 123-4567"
                  className="h-9"
                />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs">Address</Label>
                <Input
                  value={location.address}
                  onChange={(e) => updateLocation(index, 'address', e.target.value)}
                  placeholder="123 Main Street"
                  className="h-9"
                />
              </div>
              <div>
                <Label className="text-xs">City</Label>
                <Input
                  value={location.city}
                  onChange={(e) => updateLocation(index, 'city', e.target.value)}
                  placeholder="Montreal"
                  className="h-9"
                />
              </div>
              <div>
                <Label className="text-xs">Province</Label>
                <Select 
                  value={location.province}
                  onValueChange={(v) => updateLocation(index, 'province', v)}
                >
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="QC">Quebec</SelectItem>
                    <SelectItem value="ON">Ontario</SelectItem>
                    <SelectItem value="BC">British Columbia</SelectItem>
                    <SelectItem value="AB">Alberta</SelectItem>
                    <SelectItem value="MB">Manitoba</SelectItem>
                    <SelectItem value="SK">Saskatchewan</SelectItem>
                    <SelectItem value="NS">Nova Scotia</SelectItem>
                    <SelectItem value="NB">New Brunswick</SelectItem>
                    <SelectItem value="NL">Newfoundland and Labrador</SelectItem>
                    <SelectItem value="PE">Prince Edward Island</SelectItem>
                    <SelectItem value="NT">Northwest Territories</SelectItem>
                    <SelectItem value="YT">Yukon</SelectItem>
                    <SelectItem value="NU">Nunavut</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Postal Code</Label>
                <Input
                  value={location.postal_code}
                  onChange={(e) => updateLocation(index, 'postal_code', e.target.value)}
                  placeholder="H2X 1Y4"
                  className="h-9"
                />
              </div>
              <div>
                <Label className="text-xs">Email</Label>
                <Input
                  type="email"
                  value={location.email}
                  onChange={(e) => updateLocation(index, 'email', e.target.value)}
                  placeholder="montreal@company.com"
                  className="h-9"
                />
              </div>
              {!location.is_primary && (
                <div className="md:col-span-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => updateLocation(index, 'is_primary', true)}
                    className="text-xs"
                  >
                    Set as Primary Location
                  </Button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  if (prefillLoading) {
    return (
      <Dialog open={isOpen} onOpenChange={() => {}}>
        <DialogContent className="bg-white max-w-2xl">
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-[#1e3a5f]" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="onboarding-modal">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-slate-900">
            Welcome! Let's set up your profile
          </DialogTitle>
          <DialogDescription className="text-slate-600">
            Please provide your information to complete your account setup. This helps us serve you better.
          </DialogDescription>
        </DialogHeader>

        {renderStepIndicator()}

        <div className="py-4">
          {step === 1 && renderStep1()}
          {step === 2 && renderStep2()}
          {step === 3 && renderStep3()}
        </div>

        <div className="flex justify-between pt-4 border-t">
          <Button
            type="button"
            variant="outline"
            onClick={() => setStep(Math.max(1, step - 1))}
            disabled={step === 1 || loading}
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Back
          </Button>
          
          {step < 3 ? (
            <Button
              type="button"
              onClick={() => setStep(step + 1)}
              className="bg-[#1e3a5f] hover:bg-[#2d4a6f] text-white"
            >
              Next
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          ) : (
            <Button
              type="button"
              onClick={handleSubmit}
              disabled={loading}
              className="bg-green-600 hover:bg-green-700 text-white"
              data-testid="onboard-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Complete Setup
                </>
              )}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default OnboardingModal;
